import React, { useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";

import api from "../api/client";

/* Watch a shell session somebody invited you to. READ-ONLY.
 *
 * The screen-share watcher reuses ScreenShareView because the fiddly part
 * there is decoding frames onto a canvas. A shell needs an xterm mount
 * instead, and reusing DeviceShell is the wrong direction: that page's job
 * is to CREATE a session, send resizes and forward keystrokes, and a watcher
 * must do none of those. Stripping them out by prop would leave the input
 * path present and one boolean away from live.
 *
 * READ-ONLY BY CONSTRUCTION, in three independent places, because this is
 * the property that matters:
 *   - the server's observer socket runs only the broker's send_loop, so
 *     there is no forward path back into the session at all;
 *   - this component never calls ws.send();
 *   - xterm is built with disableStdin, so keystrokes are not even captured
 *     and the operator's cursor is not shared.
 *
 * Any one of those failing leaves the other two.
 */
export default function ShellWatchView({
  token,
  label,
  onClose,
}: {
  token: string;
  label: string;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<"connecting" | "live" | "closed" | "error">(
    "connecting",
  );
  const [note, setNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    let term: Terminal | null = null;
    let ws: WebSocket | null = null;

    const safeFit = () => {
      try {
        fitRef.current?.fit();
      } catch {
        /* the container can be mid-layout; the next resize will catch it */
      }
    };

    const start = () => {
      term = new Terminal({
        convertEol: true,
        // NOT cursorBlink, and NOT stdin. A blinking cursor on a terminal
        // that ignores you is an invitation to type into a void.
        cursorBlink: false,
        disableStdin: true,
        fontFamily: 'Consolas, "Cascadia Mono", "JetBrains Mono", "Liberation Mono", monospace',
        fontSize: 14,
        theme: { background: "#0a0e1a", foreground: "#e6edf3" },
        scrollback: 5000,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      termRef.current = term;
      fitRef.current = fit;
      if (containerRef.current) {
        term.open(containerRef.current);
        // Two rAF ticks: one for layout, one for xterm's renderer init.
        requestAnimationFrame(() => requestAnimationFrame(safeFit));
      }

      /* SAY THAT HISTORY IS MISSING.
       *
       * Shell sessions keep no server-side scrollback - unlike the AI
       * Terminal, which replays 256 KiB on attach - so a watcher joins
       * mid-stream and sees only what happens from now on. An empty black
       * rectangle looks identical to a broken connection, and the operator
       * being watched may have run the interesting command a minute ago. */
      term.writeln("\x1b[90m[aerogauge] Watching \x1b[0m" + label);
      term.writeln(
        "\x1b[90m[aerogauge] Read-only. You are seeing output from the moment you joined;" +
          " anything before that is not replayed.\x1b[0m",
      );

      const jwt = localStorage.getItem("token") || "";
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url =
        `${proto}//${window.location.host}/api/v1/console/shell/${token}/observe` +
        `?access_token=${encodeURIComponent(jwt)}`;
      ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setStatus("live");
      };

      ws.onmessage = (evt) => {
        if (!term) return;
        // The broker sends the agent's PTY output as text frames and binary
        // where the agent chose to; handle both rather than assuming.
        if (evt.data instanceof ArrayBuffer) {
          term.write(new Uint8Array(evt.data));
        } else if (typeof evt.data === "string") {
          term.write(evt.data);
        }
      };

      ws.onerror = () => {
        if (cancelled) return;
        setStatus("error");
      };

      ws.onclose = async (evt) => {
        if (cancelled) return;
        setStatus("closed");
        /* WHY IT ENDED, not just that it did.
         *
         * A watcher's socket closes for reasons that have nothing to do with
         * them: the operator finished, the session was superseded by a newer
         * sign-in, or the backend restarted. 54% of recorded console drops
         * were restarts, and the product knows which - so ask, rather than
         * leaving them to conclude the feature is flaky. */
        let why = evt.reason || "";
        if (evt.code === 1008 && why === "not_invited") {
          why = "Your invitation to this session is no longer valid.";
        } else {
          try {
            const r = await api.get(`/api/v1/console/shell/${token}/state`);
            if (r.data?.reason) why = r.data.reason;
          } catch {
            /* the session row may be gone entirely; keep the close reason */
          }
        }
        setNote(why || "The session ended.");
        term?.writeln(`\r\n\x1b[90m[aerogauge] ${why || "The session ended."}\x1b[0m`);
      };
    };

    start();
    const onResize = () => safeFit();
    window.addEventListener("resize", onResize);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      try {
        term?.dispose();
      } catch {
        /* ignore */
      }
      termRef.current = null;
      fitRef.current = null;
      wsRef.current = null;
    };
    // token is fixed for the life of the page (it comes from the URL) and
    // label is only ever a caption - neither may re-run this and tear down a
    // live view. Same reasoning as ScreenShareView's refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col h-full bg-[#0a0e1a]">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 text-gray-100 border-b border-gray-700">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium truncate">{label}</span>
          <span
            className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 whitespace-nowrap"
            title="A watcher cannot type, resize or paste into this session"
          >
            READ-ONLY
          </span>
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${
              status === "live"
                ? "bg-green-500/20 text-green-300 border border-green-500/40"
                : status === "connecting"
                  ? "bg-gray-700 text-gray-300"
                  : "bg-rose-500/20 text-rose-300 border border-rose-500/40"
            }`}
          >
            {status === "live" ? "WATCHING" : status.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {note && (
            <span className="text-[11px] text-gray-400 max-w-md truncate" title={note}>
              {note}
            </span>
          )}
          <button
            onClick={onClose}
            className="text-xs bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded whitespace-nowrap"
          >
            Stop watching
          </button>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 overflow-hidden p-2" />
    </div>
  );
}
